import { useMapEvents, useMap } from 'react-leaflet';
import { useCallback, useRef, useEffect } from 'react';
import type { Position } from '../../types.js';

interface MapViewControllerProps {
  onMapStateChange: (center: Position, zoom: number) => void;
  targetCenter?: Position;
  targetZoom?: number;
  shouldUpdateView?: boolean;
}

/**
 * Component to both track map state changes and programmatically update map view
 * This component listens to map events and can also update the map view when needed
 */
export const MapViewController: React.FC<MapViewControllerProps> = ({ 
  onMapStateChange,
  targetCenter,
  targetZoom,
  shouldUpdateView = false
}) => {
  const map = useMap();
  const throttleRef = useRef<number | null>(null);
  const lastUpdateRef = useRef<{ center: Position; zoom: number } | null>(null);

  const handleMapStateChange = useCallback((center: Position, zoom: number) => {
    // Throttle the updates to avoid too frequent session storage writes
    if (throttleRef.current) {
      clearTimeout(throttleRef.current);
    }
    
    throttleRef.current = window.setTimeout(() => {
      onMapStateChange(center, zoom);
      lastUpdateRef.current = { center, zoom };
    }, 300); // Update session storage max every 300ms
  }, [onMapStateChange]);

  // Effect to update map view when targetCenter/targetZoom changes
  useEffect(() => {
    if (!shouldUpdateView || !targetCenter || typeof targetZoom !== 'number') {
      return;
    }

    // Check if the target position is different from current position
    const currentCenter = map.getCenter();
    const currentZoom = map.getZoom();
    
    const centerChanged = Math.abs(currentCenter.lat - targetCenter.lat) > 0.0001 ||
                         Math.abs(currentCenter.lng - targetCenter.lng) > 0.0001;
    const zoomChanged = Math.abs(currentZoom - targetZoom) > 0.1;

    if (centerChanged || zoomChanged) {
      // Use setView to update both center and zoom simultaneously without animation
      map.setView([targetCenter.lat, targetCenter.lng], targetZoom, {
        animate: false // No animation for instant restoration
      });
    }
  }, [map, targetCenter, targetZoom, shouldUpdateView]);

  useMapEvents({
    moveend(e) {
      const map = e.target;
      const center = map.getCenter();
      const zoom = map.getZoom();
      
      handleMapStateChange(
        { lat: center.lat, lng: center.lng },
        zoom
      );
    },
    zoomend(e) {
      const map = e.target;
      const center = map.getCenter();
      const zoom = map.getZoom();
      
      handleMapStateChange(
        { lat: center.lat, lng: center.lng },
        zoom
      );
    }
  });

  return null;
};
